/**
 * Single table row for a transit result with planet name, times, depth, vmag, altitude, moon, uncertainty, source chip.
 */
import React from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Box,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckBox as CompleteIcon,
  IndeterminateCheckBox as PartialIcon,
  Cancel as CancelIcon,
  NightsStay as MoonIcon,
} from '@mui/icons-material';
import EphemerisSourceChip from './EphemerisSourceChip';
import QualityBadge from './QualityBadge';
import { formatTime, formatDuration, getLocalTime } from '../utils/timeFormatting';

const ObservabilityIndicator = ({ observability }) => (
  <Tooltip title={observability.reason}>
    <Box display="flex" alignItems="center" gap={0.5}>
      {observability.canObserve === 'complete' && <CompleteIcon color="success" fontSize="small" />}
      {observability.canObserve === 'partial' && <PartialIcon color="warning" fontSize="small" />}
      {observability.canObserve === 'no' && <CancelIcon color="error" fontSize="small" />}
      {observability.canObserve === 'unknown' && <InfoIcon color="disabled" fontSize="small" />}
      <Typography variant="caption" color="text.secondary">
        {observability.canObserve === 'complete' && 'Full'}
        {observability.canObserve === 'partial' && 'Partial'}
        {observability.canObserve === 'no' && 'No'}
        {observability.canObserve === 'unknown' && '?'}
      </Typography>
    </Box>
  </Tooltip>
);

const TimeCell = ({ time, observatory, label }) => (
  <TableCell align="center">
    <Typography variant="body2" color={label ? 'text.secondary' : undefined}>
      {formatTime(time)}
    </Typography>
    {observatory && (() => {
      const local = getLocalTime(time, observatory);
      return local.local ? (
        <Typography variant="caption" color="text.secondary">
          {local.local}
        </Typography>
      ) : null;
    })()}
  </TableCell>
);

const TransitStartCell = ({ startTime, observatory, timingUncertainty, ephemerisSource }) => (
  <TableCell align="center">
    <Box>
      <Typography variant="body2">
        {formatTime(startTime)}
      </Typography>
      {observatory && (() => {
        const local = getLocalTime(startTime, observatory);
        return local.local ? (
          <Typography variant="caption" color="text.secondary">
            {local.local}
          </Typography>
        ) : null;
      })()}
      {timingUncertainty?.minutes && (
        <Tooltip title={`Timing uncertainty: +/-${timingUncertainty.minutes.toFixed(0)} minutes (1-sigma)`}>
          <Typography variant="caption" color={
            timingUncertainty.quality === 'excellent' ? 'success.main' :
            timingUncertainty.quality === 'good' ? 'text.secondary' :
            timingUncertainty.quality === 'fair' ? 'warning.main' : 'error.main'
          }>
            +/-{timingUncertainty.minutes.toFixed(0)}m
          </Typography>
        </Tooltip>
      )}
      {timingUncertainty?.warning && (
        <Tooltip title={timingUncertainty.warning}>
          <WarningIcon color="warning" fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
        </Tooltip>
      )}
      <EphemerisSourceChip source={ephemerisSource} />
    </Box>
  </TableCell>
);

const MoonCell = ({ transit }) => {
  if (transit.moon_separation == null) {
    return (
      <TableCell align="center">
        <Typography variant="caption" color="text.secondary">N/A</Typography>
      </TableCell>
    );
  }

  const moonColor = transit.moon_altitude <= 0 ? 'default' :
    transit.required_moon_separation != null
      ? (transit.moon_separation >= transit.required_moon_separation ? 'success' : 'error')
      : (transit.moon_separation >= 60 ? 'success' :
         transit.moon_separation >= 30 ? 'warning' : 'error');

  return (
    <TableCell align="center">
      <Tooltip title={
        `Moon: ${transit.moon_illumination}% illuminated, ` +
        `alt ${transit.moon_altitude}` +
        (transit.required_moon_separation != null
          ? `, required >=${transit.required_moon_separation}`
          : '') +
        (transit.moon_altitude <= 0 ? ' (below horizon)' : '')
      }>
        <Chip
          label={`${transit.moon_separation.toFixed(0)}`}
          size="small"
          color={moonColor}
          icon={<MoonIcon />}
        />
      </Tooltip>
    </TableCell>
  );
};

const TransitResultRow = ({
  transit,
  index,
  isSelected,
  hasConflict,
  hasSelectedConflict,
  overlapInfo,
  observability,
  observatory,
  baselineBefore,
  baselineAfter,
  onSelect,
  onShowDetails,
}) => {
  const midpointTime = new Date(transit.transit.time_utc);
  const halfDurationMs = (transit.transit.duration_hours || 0) * 3600000 / 2;
  const startTime = new Date(midpointTime.getTime() - halfDurationMs);
  const endTime = new Date(midpointTime.getTime() + halfDurationMs);

  return (
    <TableRow
      key={index}
      hover
      selected={isSelected}
      onClick={(e) => {
        if (e.target.closest('.detail-button')) return;
        onSelect();
      }}
      sx={{
        cursor: 'pointer',
        ...(hasConflict && {
          backgroundColor: 'rgba(211, 47, 47, 0.08)',
          '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.14)' },
        }),
        ...(hasSelectedConflict && {
          backgroundColor: 'rgba(237, 108, 2, 0.08)',
          '&:hover': { backgroundColor: 'rgba(237, 108, 2, 0.14)' },
        }),
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox checked={isSelected} />
      </TableCell>
      <TableCell>
        <ObservabilityIndicator observability={observability} />
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" fontWeight="bold">
            {transit.exoplanet.name}
          </Typography>
          {transit.exoplanet.quality_grade && (
            <QualityBadge
              qualityGrade={transit.exoplanet.quality_grade}
              qualityScore={transit.exoplanet.quality_score}
              ephemerisSource={transit.exoplanet.ephemeris_source}
            />
          )}
          {hasConflict && (
            <Tooltip title={`Overlaps with: ${overlapInfo.overlapsWithSelected.join(', ')}`}>
              <WarningIcon sx={{ color: 'error.main', fontSize: 16 }} />
            </Tooltip>
          )}
          {hasSelectedConflict && (
            <Tooltip title={`Overlaps with selected: ${overlapInfo.selectedOverlapsSelected.join(', ')}`}>
              <WarningIcon sx={{ color: 'warning.main', fontSize: 16 }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>{transit.exoplanet.host_star}</TableCell>
      <TableCell align="center">
        <Chip
          label={transit.exoplanet.vmag?.toFixed(1) || 'N/A'}
          size="small"
          color={transit.exoplanet.vmag < 12 ? 'success' : 'default'}
        />
      </TableCell>
      <TimeCell
        time={new Date(startTime.getTime() - baselineBefore * 60000)}
        observatory={observatory}
        label
      />
      <TransitStartCell
        startTime={startTime}
        observatory={observatory}
        timingUncertainty={transit.transit.timing_uncertainty}
        ephemerisSource={transit.exoplanet.ephemeris_source}
      />
      <TableCell align="center">
        <Typography variant="body2">
          {transit.transit.duration_hours ? formatTime(endTime) : 'N/A'}
        </Typography>
        {observatory && transit.transit.duration_hours && (() => {
          const local = getLocalTime(endTime, observatory);
          return local.local ? (
            <Typography variant="caption" color="text.secondary">
              {local.local}
            </Typography>
          ) : null;
        })()}
      </TableCell>
      <TimeCell
        time={transit.transit.duration_hours
          ? new Date(endTime.getTime() + baselineAfter * 60000)
          : null}
        observatory={transit.transit.duration_hours ? observatory : null}
        label
      />
      <TableCell align="center">
        {formatDuration(transit.transit.duration_hours)}
      </TableCell>
      <TableCell align="center">
        <Chip
          label={transit.transit.depth_percent?.toFixed(2) || 'N/A'}
          size="small"
          color={transit.transit.depth_percent > 1 ? 'primary' : 'default'}
        />
      </TableCell>
      <TableCell align="center">
        {transit.transit.altitude ? (
          <Tooltip title={
            `Ingress: ${(transit.transit.altitude_ingress ?? transit.transit.altitude).toFixed(1)}` +
            ` | Mid: ${transit.transit.altitude.toFixed(1)}` +
            ` | Egress: ${(transit.transit.altitude_egress ?? transit.transit.altitude).toFixed(1)}`
          }>
            <span>{transit.transit.altitude.toFixed(0)}</span>
          </Tooltip>
        ) : 'N/A'}
      </TableCell>
      <MoonCell transit={transit.transit} />
      <TableCell align="center">
        <IconButton
          size="small"
          className="detail-button"
          onClick={() => onShowDetails(transit)}
          color="primary"
        >
          <InfoIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default TransitResultRow;
